import React, { useState, useEffect } from "react";
import PageContainer from "../../components/PageContainer/PageContainer";
import styled from "styled-components";
import Footer from "../../components/Footer";
import Button from "../../components/Button";
import Text from "../../components/Text";
import Img from "../../components/Img";
import MysteryBoxPopup from "./MysteryBoxPopup";
import PendingItemsPopup from "./PendingItemsPopup";
import { MYSTERY_BOXES, MISTERY_BOX_ADDRESS, miners } from "../../utils/constants";
import "./mystery-box.css";
import { ethers } from "ethers";
import contractABI from "../../abis/mysterybox.abi.json";
import { Event } from "ethers";

const HomeContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    padding: 4rem;
    max-width: 1400px;
    margin: 0 auto;
    gap: 4rem;

    @media (max-width: 768px) {
        padding: 2rem 0.5rem;
        gap: 2rem;
    }
`;

const ContentSection = styled.div`
    flex: 1;
    max-width: 800px;
    text-align: center;

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const Title = styled.h1`
    font-size: 3em;
    line-height: 1.2;
    margin-bottom: 2rem;
    color: #507c83;
    text-transform: uppercase;
    font-family: "Press Start 2P", monospace;
    letter-spacing: 2px;
    white-space: pre-line;
    text-align: center;

    @media (max-width: 768px) {
        font-size: 2em;
        margin-bottom: 1.5rem;
    }
`;

const Subtitle = styled.p`
    font-size: 1.5em;
    margin-bottom: 3rem;
    color: white;
    font-family: "Press Start 2P", monospace;
    letter-spacing: 1px;
    line-height: 1.4;
    text-transform: uppercase;
    max-width: 800px;
    text-align: center;
    margin: 0 auto;

    @media (max-width: 768px) {
        font-size: 1em;
        margin-bottom: 2rem;
        padding: 0 1rem;
    }
`;

const BoxesContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    width: 100%;
    max-width: 1200px;
    padding: 0;

    @media (min-width: 1024px) {
        flex-direction: row;
        flex-wrap: wrap;
        justify-content: center;
        padding: 32px;
        gap: 24px;
    }
`;

const InfoButton = styled.button`
    position: absolute;
    top: 12px;
    right: 12px;
    background: #507c83;
    color: white;
    border: none;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 20px;
    font-weight: bold;
    transition: background 0.2s ease-in-out;

    &:hover {
        background: #6b9ba3;
    }
`;

const BoxTitle = styled(Text)`
    color: #507c83 !important;
    margin-bottom: 16px;
`;

const Price = styled(Text)`
    font-size: 1.3em;
    color: #222;
    font-family: "Press Start 2P", monospace;
    margin-bottom: 1.5em;
    margin-top: 0.2em;
`;

const BoxButton = styled(Button)`
    /* Removed extra margin-top for a more compact look */
`;

const ClaimButton = styled.button`
    background: white;
    display: unset;
    color: black;
    font-family: "Press Start 2P", monospace;
    padding: 1rem 2rem;
    margin-top: 2rem;
    font-size: 1.2em;
    border: none;
    outline: none;
    box-shadow: none;
    cursor: pointer;
    &:hover {
        background: #e0e0e0;
    }

    @media (max-width: 768px) {
        padding: 0.8rem 1.5rem;
        font-size: 1em;
        width: 90%;
        max-width: 300px;
    }
`;

export function getMysteryBoxContract(signerOrProvider: ethers.Signer | ethers.providers.Provider) {
  return new ethers.Contract(MISTERY_BOX_ADDRESS, contractABI, signerOrProvider);
}

const MysteryBoxPage = () => {
    const [showPopup, setShowPopup] = useState(false);
    const [showOpenBoxPopup, setShowOpenBoxPopup] = useState(false);
    const [showPendingItems, setShowPendingItems] = useState(false);
    const [popupType, setPopupType] = useState<'animals' | 'flexshop' | 'mix'>('animals');
    const [boxPrices, setBoxPrices] = useState<{ [key: number]: string }>({});
    const [loadingPrices, setLoadingPrices] = useState(true);
    const [wonMinerId, setWonMinerId] = useState<number | null>(null);

    const handleInfoClick = (type: 'animals' | 'flexshop' | 'mix') => {
        setPopupType(type);
        setShowPopup(true);
    };

    const handleOpenBox = (type: 'animals' | 'flexshop' | 'mix', boxId: number) => {
        setPopupType(type);
        setShowOpenBoxPopup(true);
    };

    const handleBoxOpened = async (minerId: number | null) => {
        setWonMinerId(minerId);
    };

    useEffect(() => {
        async function fetchPrices() {
            try {
                if (!(window as any).ethereum) return;
                const provider = new ethers.providers.Web3Provider((window as any).ethereum);
                const contract = getMysteryBoxContract(provider);

                // Assuming box IDs are 1, 2, 3
                const ids = [1, 2, 3];
                const prices: { [key: number]: string } = {};
                for (const id of ids) {
                    const box = await contract.mysteryBoxes(id);
                    prices[id] = ethers.utils.formatUnits(box.boxPrice, 18); // Adjust decimals if needed
                }
                setBoxPrices(prices);
            } catch (err) {
                console.error("Failed to fetch box prices", err);
            } finally {
                setLoadingPrices(false);
            }
        }
        fetchPrices();
    }, []);

    return (
        <PageContainer>
            <main style={{
                minHeight: '100vh',
                color: '#fff',
                fontFamily: "Press Start 2P, monospace"
            }}>
                <HomeContainer>
                    <ContentSection>
                        <Title>MYSTERY BOXES</Title>
                        <Subtitle>
                            DISCOVER RARE ITEMS AND EXCLUSIVE REWARDS IN OUR MYSTERY BOXES.
                            EACH BOX CONTAINS UNIQUE ITEMS FROM OUR COLLECTION.
                        </Subtitle>
                        <ClaimButton
                            onClick={() => setShowPendingItems(true)}
                        >
                            CLAIM PENDING ITEMS
                        </ClaimButton>
                    </ContentSection>

                    <BoxesContainer>
                        {MYSTERY_BOXES.map((box) => (
                            <div className="animal-card-container" key={box.type}>
                                <div className="mysterybox-pixel-animal-container">
                                    <div className="mysterybox-pixel-animal-box">
                                        <InfoButton onClick={() => handleInfoClick(box.type as any)}>i</InfoButton>
                                        <div className="box-image">
                                            <img src={box.image} alt={box.title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                        </div>
                                        <BoxTitle
                                            as="p"
                                            className="text-[28px] font-normal font-upheaval text-center"
                                            style={{ fontSize: '2em' }}
                                        >
                                            {box.title}
                                        </BoxTitle>
                                        <Price
                                            as="p"
                                            className="text-[18px] font-normal font-upheaval text-white text-center"
                                            style={{ color: '#FFFFFF' }}
                                        >
                                            PRICE: {loadingPrices ? "..." : boxPrices[box.id] || "?"} MILK
                                        </Price>
                                        <BoxButton
                                            variant="pixelAction"
                                            size="md"
                                            shape="square"
                                            className="min-w-[160px] px-5 py-2 font-upheaval text-[1.3em]"
                                            onClick={() => handleOpenBox(box.type as any, box.id)}
                                        >
                                            OPEN BOX
                                        </BoxButton>
                                    </div>
                                    <div className="pixel-scanlines"></div>
                                </div>
                            </div>
                        ))}
                    </BoxesContainer>
                </HomeContainer>
            </main>
            <Footer />
            {showPopup && (
                <MysteryBoxPopup
                    type={popupType}
                    onClose={() => setShowPopup(false)}
                    boxId={MYSTERY_BOXES.find(box => box.type === popupType)?.id || 1}
                />
            )}
            {showOpenBoxPopup && (
                <MysteryBoxPopup
                    type={popupType}
                    onClose={() => setShowOpenBoxPopup(false)}
                    isOpenBox={true}
                    minerId={wonMinerId}
                    onMinerWon={handleBoxOpened}
                    boxId={MYSTERY_BOXES.find(box => box.type === popupType)?.id || 1}
                />
            )}
            {showPendingItems && (
                <PendingItemsPopup onClose={() => setShowPendingItems(false)} />
            )}
        </PageContainer>
    );
}

export default MysteryBoxPage; 